import { useEffect } from "react";
import { getDefaultStore, useAtomValue } from "jotai";
import logger, { Logger } from "../../../utils/logger";
import { isOnlineAtom, isSafeToDeleteAudioChunksAtom, userSettingsAtom } from "../../../model/atoms";
import { readBlobAsDataUri } from "../../utils/clipboard/utils";
import { createSingleQueueAsync } from "../../../utils/runners";
import { urlUpload } from "../../../utils/environment";
import { isOfflineError } from "../../../utils/errorCheckers";
import { TRANSCRIPTION_LANGUAGES } from "../../../../shared/transcription";
import { ApiClient } from "../../../api/client";
import { db } from "../../../storage/storage";
import { Stage } from "./audioTypes";
import { useLocalAudio } from "./hooks";

export const audioLogsPrefix = "audio";
export const audioInsertPrefix = "audio-insert";

// This function creates the key used to store an audio file in the IndexedDB and in S3
function getAudioInsertPrefix(audioId?: string, fragmentIndex?: number | string) {
  let prefix = audioInsertPrefix;
  if (audioId) prefix += `-${audioId}`;
  if (fragmentIndex !== undefined) prefix += `-${fragmentIndex}`;
  return prefix;
}

export function audioChunkBlobKey(audioId: string, fragmentIndex: number | string) {
  return getAudioInsertPrefix(audioId, fragmentIndex) + ".mp3";
}

export function audioChunkDurationKey(audioId: string, fragmentIndex: number | string) {
  return getAudioInsertPrefix(audioId, fragmentIndex) + "-duration";
}

function audioChunkBlobUrl(audioId: string, fragmentIndex: number | string) {
  return `${urlUpload}/${audioChunkBlobKey(audioId, fragmentIndex)}`;
}

export function getTranscriptionLanguage(transcriptLanguageOverride?: string | null) {
  // Override is set by user in the audio insert UI for this specific recording and takes precedence above all else
  if (!!transcriptLanguageOverride && Object.keys(TRANSCRIPTION_LANGUAGES).includes(transcriptLanguageOverride)) {
    return transcriptLanguageOverride;
  }

  // User has a default transcription language in their settings
  return getDefaultStore().get(userSettingsAtom).defaultTranscriptionLanguage;
}

export async function uploadAudioChunk(audioId: string, chunk: number, blob: Blob) {
  const fileDataUri = await readBlobAsDataUri(blob);
  const apiClient = ApiClient();
  try {
    await apiClient.files.upload(fileDataUri, audioChunkBlobKey(audioId, chunk));
  } catch (error) {
    throw new Error(`Uploading failed, reason: ${error instanceof Error ? error.message : "Unknown"}`);
  }
}

export function getAudioURLs(
  audioId: string,
  stage: Stage,
  localAudio: NonNullable<ReturnType<typeof useLocalAudio>>,
  chunkCount: number,
  durations: number[] | null,
) {
  if (stage === Stage.Recording) return [];
  // Always prefer the local audio URLs, since "interrupted" audio is hard to distinguish from legacy audio (chunkCount is also 0)
  if (localAudio.length !== 0) return localAudio.map(({ blob, ...rest }) => rest);
  // Legacy audio uploads have no chunkCount property so the default value of 0 is used by Prosemirror
  if (chunkCount === 0) {
    return [{ url: `${urlUpload}/images/audio-insert-${audioId}`, duration: null }];
  }

  if (chunkCount === null || durations === null) {
    return [];
  }

  return Array.from(new Array(Math.min(chunkCount, durations.length)), (_, i) => ({
    url: audioChunkBlobUrl(audioId, i),
    duration: durations?.[i] ?? 0,
  }));
}

export async function getLocalAudio(audioId: string) {
  const audioChunks = await db.audioChunks.getAllByAudioId(audioId);
  if (audioChunks.length === 0) return [];

  const maxChunkIndex = audioChunks.reduce((max, chunk) => Math.max(max, chunk.chunkIndex), 0);

  if (maxChunkIndex !== audioChunks.length - 1) {
    logger.error(new Error("Broken chunks of audio detected"), {
      namespace: audioLogsPrefix,
      context: { audioId, audioChunks },
    });
    return [];
  }

  return audioChunks.map((audioChunk) => {
    return {
      blob: audioChunk.blob,
      duration: audioChunk.duration,
    };
  });
}

export async function deleteLocalAudio(audioId: string) {
  await db.audioChunks.deleteAllByAudioId(audioId);
}

/**
 * Try uploading local audio chunks and refresh the editor if successful.
 *
 * Only one upload can be running at a time. If there is already an upload
 * running, this will be queued and run after the current upload finishes. If
 * there is already an upload queued, this is a no-op.
 */
export const debouncedUploadAllAudio = createSingleQueueAsync(
  async (logger: Logger) => {
    try {
      const audioChunks = await db.audioChunks.getAllDirty();

      logger.info(`Uploading ${audioChunks.length} audio chunks`);

      await Promise.all(
        audioChunks.map(async (audioChunk) => {
          await uploadAudioChunk(audioChunk.audioId, audioChunk.chunkIndex, audioChunk.blob);
          await db.audioChunks.update({ id: audioChunk.id, isDirty: 0 });
        }),
      );

      logger.info(`Finished uploading all local audio chunks`);

      //Only delete it on startup of the app
      //Todo: Ideally we want to delete all stale chunks when there is no recording but
      //it's too messy to implement with audioRecordingCoordinator.
      const store = getDefaultStore();
      if (!store.get(isSafeToDeleteAudioChunksAtom)) {
        return;
      }
      store.set(isSafeToDeleteAudioChunksAtom, false);
      const audioIds = await db.audioChunks.getUploadedAudioIds();
      logger.info(`Deleting ${audioIds.length} uploaded audio chunks`);
      await Promise.all(
        audioIds.map(async (audioId) => {
          await db.audioChunks.deleteAllByAudioId(audioId);
        }),
      );

      //Todo: Maybe add a logger if something goes wrong?

      return true;
    } catch (error) {
      if (isOfflineError(error)) {
        logger.info(`Not uploading audio chunks because we're offline`);
      } else {
        logger.error(`Error uploading all local audio chunks`, { error });
      }
      return false;
    }
  },
  { name: `${audioLogsPrefix}/uploadAudio` },
);

/**
 * Upload all local audio chunks whenever we come back online
 */
export function useAutoUploadAudio() {
  const isOnline = useAtomValue(isOnlineAtom);
  // Retry when we come online
  useEffect(() => {
    if (!isOnline) return;
    debouncedUploadAllAudio();
  }, [isOnline]);
}

/**
 * Converts ISO UTC DateTime to "MM-DD-YYYY HH-MM-SS"
 * @param datetime
 */

export const formatISOToUSDateTime = (datetime: string) => {
  const [creationDate, creationTime] = new Date(datetime).toLocaleString("en-US").split(", ");

  const creationTimeParts = creationTime.split(" ")[0].split(":");

  if (creationTime.endsWith("PM")) {
    creationTimeParts[0] = `${+creationTimeParts[0] + 12}`;
  }

  if (Number(creationTimeParts[0]) < 10) {
    creationTimeParts[0] = `0${creationTimeParts[0]}`;
  }

  return creationDate.split("/").join("-") + " " + creationTimeParts.join("-");
};

export const decodeBase64ToMp3 = (base64EncodedString: string) => {
  const binaryString = window.atob(base64EncodedString);
  const len = binaryString.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }

  return new Blob([bytes], { type: "audio/mp3" });
};
